import React, { useEffect } from 'react';
import { FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa'; // FaSpinner rimosso

// --- MAPPA DIMENSIONI ---
const SIZES = {
    small: 'modal-sm',
    medium: '', 
    large: 'modal-lg',
    xl: 'modal-xl'
};

// --- BASE MODAL (Componente Genitore) ---
export const BaseModal = ({ 
    show, 
    onClose, 
    title, 
    children, 
    footer, 
    type = 'primary', 
    size = 'medium',
    hideButtons = false,
    autoCloseDuration = 0 
}) => {
    
    // Logica Auto-Close
    useEffect(() => {
        let timer;
        if (show && hideButtons && autoCloseDuration > 0) {
            timer = setTimeout(() => {
                if (onClose) onClose();
            }, autoCloseDuration);
        }
        return () => clearTimeout(timer);
    }, [show, hideButtons, autoCloseDuration, onClose]);

    if (!show) return null;

    // Colori Header
    const headerColors = {
        success: 'bg-success text-white',
        error: 'bg-danger text-white',
        warning: 'bg-warning text-dark',
        info: 'bg-info text-white',
        primary: 'bg-primary text-white',
        neutral: 'bg-light text-dark',
        loading: 'bg-primary text-white'
    };

    return (
        <>
            <div className="modal-backdrop show" style={{ opacity: 0.5, zIndex: 1040 }}></div>
            <div className="modal d-block fade show" tabIndex="-1" style={{ zIndex: 1050 }}>
                <div className={`modal-dialog modal-dialog-centered ${SIZES[size] || ''}`}>
                    <div className="modal-content shadow-lg border-0">
                        
                        <div className={`modal-header ${headerColors[type] || headerColors.primary}`}>
                            <h5 className="modal-title fw-bold d-flex align-items-center">
                                {title}
                            </h5>
                            {/* Mostra la X solo se NON c'è l'auto-close o se i bottoni sono visibili */}
                            {(!hideButtons || autoCloseDuration === 0) && onClose && (
                                <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
                            )}
                        </div>

                        <div className="modal-body p-4 text-center">
                            {children}
                        </div>

                        {!hideButtons && footer && (
                            <div className="modal-footer justify-content-center bg-light border-0 pb-4">
                                {footer}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

// --- 1. SUCCESS MODAL ---
export const SuccessModal = ({ 
    show, onClose, title = "Successo!", message, size = 'medium', 
    onConfirm, confirmText = "Ottimo", 
    hideButtons = false, autoCloseDuration = 0 
}) => {
    return (
        <BaseModal 
            show={show} onClose={onClose} title={title} type="success" size={size} 
            hideButtons={hideButtons} autoCloseDuration={autoCloseDuration}
            footer={
                <button className="btn btn-success px-5 fw-bold rounded-pill" onClick={onConfirm || onClose}>
                    {confirmText}
                </button>
            }
        >
            <div className="mb-3"><FaCheckCircle className="text-success display-1" /></div>
            <h4 className="fw-bold text-success mb-3">{title}</h4>
            <div className="fs-5 text-muted">{message}</div>
        </BaseModal>
    );
};

// --- 2. ERROR MODAL ---
export const ErrorModal = ({ 
    show, onClose, title = "Errore", message, size = 'medium',
    hideButtons = false, autoCloseDuration = 0 
}) => {
    return (
        <BaseModal 
            show={show} onClose={onClose} title={title} type="error" size={size}
            hideButtons={hideButtons} autoCloseDuration={autoCloseDuration}
            footer={
                <button className="btn btn-secondary px-4 rounded-pill" onClick={onClose}>Chiudi</button>
            }
        >
            <div className="mb-3"><FaTimesCircle className="text-danger display-1" /></div>
            <h4 className="fw-bold text-danger mb-3">Ops! Qualcosa non va.</h4>
            <div className="text-muted">{message}</div>
        </BaseModal>
    );
};

// --- 3. CONFIRM MODAL ---
export const ConfirmModal = ({ 
    show, onClose, onConfirm, title = "Sei sicuro?", message, size = 'medium',
    confirmText = "Conferma", cancelText = "Annulla", isDestructive = false, confirmColor
}) => {
    const btnClass = confirmColor 
        ? confirmColor 
        : (isDestructive ? 'btn-danger' : 'btn-primary');

    return (
        <BaseModal 
            show={show} onClose={onClose} title="Richiesta Conferma" type={isDestructive ? 'warning' : 'primary'} size={size}
            footer={
                <div className="d-flex gap-3">
                    <button className="btn btn-outline-secondary px-4 rounded-pill" onClick={onClose}>{cancelText}</button>
                    <button 
                        className={`btn px-4 fw-bold rounded-pill text-white ${btnClass}`} 
                        onClick={() => { onConfirm(); onClose(); }}
                    >
                        {confirmText}
                    </button>
                </div>
            }
        >
            <div className="mb-3">
                <FaExclamationTriangle className={`display-1 ${isDestructive ? 'text-warning' : 'text-primary'}`} />
            </div>
            <h4 className="fw-bold mb-2">{title}</h4>
            <div className="text-muted mb-4">{message}</div>
        </BaseModal>
    );
};

// --- 4. INFO MODAL ---
export const InfoModal = ({ 
    show, onClose, title = "Informazione", message, size = 'medium',
    hideButtons = false, autoCloseDuration = 0 
}) => {
    return (
        <BaseModal 
            show={show} onClose={onClose} title={title} type="info" size={size}
            hideButtons={hideButtons} autoCloseDuration={autoCloseDuration}
            footer={
                <button className="btn btn-info text-white px-4 rounded-pill fw-bold" onClick={onClose}>Ho capito</button>
            }
        >
            <div className="mb-3"><FaInfoCircle className="text-info display-1" /></div>
            <div className="fs-5">{message}</div>
        </BaseModal>
    );
};

// --- 5. LOADING MODAL (Modificato per usare Bootstrap Spinner) ---
export const LoadingModal = ({ 
    show, title = "Elaborazione in corso...", message, progress = 0 
}) => {
    return (
        <BaseModal 
            show={show} 
            title={title} 
            type="loading" 
            size="medium"
            hideButtons={true} 
        >
            <div className="mb-4 d-flex justify-content-center">
                {/* Bootstrap Spinner: Gira automaticamente grazie al CSS di Bootstrap */}
                <div className="spinner-border text-primary" style={{width: '3.5rem', height: '3.5rem'}} role="status">
                    <span className="visually-hidden">Caricamento...</span>
                </div>
            </div>
            
            <h5 className="mb-3 fw-bold text-dark">{message}</h5>
            
            <div className="progress" style={{ height: '25px' }}>
                <div 
                    className="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
                    role="progressbar" 
                    style={{ width: `${progress}%` }} 
                    aria-valuenow={progress} 
                    aria-valuemin="0" 
                    aria-valuemax="100"
                >
                    {progress}%
                </div>
            </div>
        </BaseModal>
    );
};